export const STALE_DEPLOYMENT_RELOAD_KEY = "pleasur:stale-deployment-reload-at"
export const STALE_DEPLOYMENT_RELOAD_COOLDOWN_MS = 15_000

type StorageLike = {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
}

let reloadInFlight = false
let recoveryBlocked = false

export function isStaleChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false

  const name = readStringField(error, "name")
  const message = readStringField(error, "message")
  const digest = readStringField(error, "digest")
  const lower = `${message} ${digest}`.toLowerCase()

  if (name === "ChunkLoadError") return true
  if (/loading chunk\s+\S+[\s\S]*\bfailed\b/.test(lower)) return true
  if (lower.includes("failed to load chunk")) return true
  if (lower.includes("failed to fetch dynamically imported module")) return true
  if (lower.includes("error loading dynamically imported module")) return true
  if (lower.includes("importing a module script failed")) return true

  return false
}

/**
 * Any deploy-skew error that trips a Next.js error boundary after a Coolify
 * rollout: a stale chunk / dynamic import (see `isStaleChunkLoadError`) or a
 * stale Server Action call. Used for boundary recovery and to keep this benign,
 * self-healing noise out of Error tracking.
 */
export function isStaleDeploymentError(error: unknown): boolean {
  if (isStaleChunkLoadError(error)) return true
  if (!error || typeof error !== "object") return false

  const name = readStringField(error, "name")
  const message = readStringField(error, "message")
  const digest = readStringField(error, "digest")
  const lower = `${message} ${digest}`.toLowerCase()

  if (name === "UnrecognizedActionError") return true
  if (lower.includes("failed to find server action")) return true
  if (lower.includes("older or newer deployment")) return true
  if (
    lower.includes("was not found on the server") &&
    lower.includes("server action")
  ) {
    return true
  }

  return false
}

export function staleDeploymentErrorIdentity(error: {
  name?: string
  message?: string
  digest?: unknown
}): string {
  const digest = typeof error.digest === "string" ? error.digest : ""
  return `${error.name ?? ""}\n${error.message ?? ""}\n${digest}`
}

/** Reload CTA is only for a current stale error whose auto-reload was blocked. */
export function nextShowReloadCta(
  isStale: boolean,
  autoReloadClaimed: boolean
): boolean {
  return isStale && !autoReloadClaimed
}

/**
 * Claim the single auto-reload. Returns true when the caller should
 * `window.location.reload()`. Returns false when a reload already ran in this
 * tab within the cooldown (or storage is unavailable), so the UI can show a
 * manual reload CTA instead of looping.
 */
export function takeStaleDeploymentReload(
  now = Date.now(),
  storage?: StorageLike
): boolean {
  if (reloadInFlight) return false

  const store = storage ?? getSessionStorage()
  if (!store) return false

  try {
    const last = Number(store.getItem(STALE_DEPLOYMENT_RELOAD_KEY) ?? "")
    if (
      Number.isFinite(last) &&
      last > 0 &&
      now - last < STALE_DEPLOYMENT_RELOAD_COOLDOWN_MS
    ) {
      return false
    }

    store.setItem(STALE_DEPLOYMENT_RELOAD_KEY, String(now))
    reloadInFlight = true
    return true
  } catch {
    return false
  }
}

/**
 * Claim the single auto-reload for a stale-deployment error and, when claimed,
 * reload the tab. Returns "reloaded" when the reload fired, or "blocked" when a
 * reload already ran in this tab within the cooldown (or storage is
 * unavailable). Returns "in-flight" while navigation is already underway.
 * A blocked caller can surface a manual reload path instead of
 * looping. Shared by the render-boundary recovery hook and the global
 * out-of-render listener so both recover identically.
 */
export function performStaleDeploymentReload(): "reloaded" | "in-flight" | "blocked" {
  if (reloadInFlight) return "in-flight"
  if (takeStaleDeploymentReload()) {
    clearStaleDeploymentRecoveryBlocked()
    window.location.reload()
    return "reloaded"
  }
  return "blocked"
}

export function markStaleDeploymentRecoveryBlocked(): void {
  recoveryBlocked = true
}

/** Keep the prompt across listener remounts in this document only. */
export function isStaleDeploymentRecoveryBlocked(): boolean {
  return recoveryBlocked
}

export function clearStaleDeploymentRecoveryBlocked(): void {
  recoveryBlocked = false
}

export function reloadStaleDeploymentManually(): void {
  clearStaleDeploymentRecoveryBlocked()
  window.location.reload()
}

export function resetStaleDeploymentReloadGuardForTests(): void {
  reloadInFlight = false
}

function getSessionStorage(): StorageLike | null {
  if (typeof window === "undefined") return null
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

function readStringField(value: object, key: string): string {
  if (!(key in value)) return ""
  const field = (value as Record<string, unknown>)[key]
  return typeof field === "string" ? field : ""
}
